import { useEffect, useMemo, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { useProductsList } from "@/hooks/use-catalog";
import type {
	FlashSale,
	FlashSaleDiscountType,
} from "@/lib/promotions/flash-sales-api";
import {
	useCreateFlashSale,
	useUpdateFlashSale,
} from "@/lib/promotions/flash-sales-hooks";

interface FlashSaleModalProps {
	isOpen: boolean;
	onClose: () => void;
	initialData?: FlashSale | null;
	onSuccess?: () => void;
}

export function FlashSaleModal({
	isOpen,
	onClose,
	initialData,
	onSuccess,
}: FlashSaleModalProps) {
	const isEditing = Boolean(initialData);

	const [activeTab, setActiveTab] = useState<"product" | "schedule">("product");

	const [name, setName] = useState("");
	const [productId, setProductId] = useState("");
	const [discountType, setDiscountType] = useState<FlashSaleDiscountType>("PERCENT");
	const [discountValue, setDiscountValue] = useState<number | "">(20);
	const [startsAt, setStartsAt] = useState("");
	const [expiresAt, setExpiresAt] = useState("");
	const [stockLimit, setStockLimit] = useState<number | "">(0);
	const [priority, setPriority] = useState<number | "">(10);
	const [isActive, setIsActive] = useState(true);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const { data: productsData } = useProductsList({ pageSize: 100 });
	const products = useMemo(() => productsData?.items || [], [productsData?.items]);

	const createMutation = useCreateFlashSale();
	const updateMutation = useUpdateFlashSale();

	const isPending = createMutation.isPending || updateMutation.isPending;

	const selectedProduct = useMemo(() => {
		return products.find((p) => p.id === productId);
	}, [products, productId]);

	const calculatedFlashPrice = useMemo(() => {
		if (!selectedProduct) return 0;
		const basePrice = Number(selectedProduct.basePrice || 0);
		const val = discountValue === "" ? 0 : Number(discountValue);

		if (discountType === "FIXED_PRICE") return val;
		if (discountType === "PERCENT") {
			const discount = Math.round((basePrice * val) / 100);
			return Math.max(0, basePrice - discount);
		}
		return Math.max(0, basePrice - val);
	}, [selectedProduct, discountType, discountValue]);

	useEffect(() => {
		if (!isOpen) return;

		if (initialData) {
			setName(initialData.name);
			setProductId(initialData.productId);
			setDiscountType(initialData.discountType);
			setDiscountValue(initialData.discountValue);
			setPriority(initialData.priority ?? 10);
			setStockLimit(initialData.stockLimit || 0);
			setIsActive(initialData.isActive);
			setStartsAt(
				initialData.startsAt
					? new Date(initialData.startsAt).toISOString().slice(0, 16)
					: "",
			);
			setExpiresAt(
				initialData.expiresAt
					? new Date(initialData.expiresAt).toISOString().slice(0, 16)
					: "",
			);
		} else {
			setName("");
			setProductId("");
			setDiscountType("PERCENT");
			setDiscountValue(20);
			setPriority(10);
			setStockLimit(0);
			setIsActive(true);
			// Par défaut: début dans 5 min, fin dans 24h
			const now = new Date();
			const start = new Date(now.getTime() + 5 * 60 * 1000);
			const end = new Date(now.getTime() + 24 * 3600 * 1000);
			setStartsAt(start.toISOString().slice(0, 16));
			setExpiresAt(end.toISOString().slice(0, 16));
		}
		setErrorMessage(null);
		setActiveTab("product");
	}, [initialData, isOpen]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setErrorMessage(null);

		if (!name.trim()) {
			setErrorMessage("Le nom de la vente flash est obligatoire.");
			setActiveTab("product");
			return;
		}

		if (!productId) {
			setErrorMessage("Veuillez sélectionner un produit.");
			setActiveTab("product");
			return;
		}

		if (discountValue === "" || Number(discountValue) < 0) {
			setErrorMessage("La valeur de la réduction doit être supérieure ou égale à 0.");
			setActiveTab("product");
			return;
		}

		if (!startsAt || !expiresAt) {
			setErrorMessage("Les dates de début et de fin sont obligatoires.");
			setActiveTab("schedule");
			return;
		}

		if (new Date(startsAt) >= new Date(expiresAt)) {
			setErrorMessage("La date de début doit être strictement antérieure à la date de fin.");
			setActiveTab("schedule");
			return;
		}

		const payload = {
			name: name.trim(),
			productId,
			discountType,
			discountValue: Number(discountValue),
			startsAt: new Date(startsAt).toISOString(),
			expiresAt: new Date(expiresAt).toISOString(),
			stockLimit: stockLimit !== "" ? Number(stockLimit) : 0,
			priority: priority !== "" ? Number(priority) : 10,
			isActive,
		};

		try {
			if (isEditing && initialData) {
				await updateMutation.mutateAsync({ id: initialData.id, data: payload });
			} else {
				await createMutation.mutateAsync(payload);
			}
			onSuccess?.();
			onClose();
		} catch (err: unknown) {
			const msg =
				err && typeof err === "object" && "message" in err
					? String((err as { message: string }).message)
					: "Une erreur est survenue lors de l'enregistrement de la vente flash.";
			setErrorMessage(msg);
		}
	};

	const labelStyle = {
		display: "block",
		fontSize: 12,
		fontWeight: 600,
		color: "#475569",
		marginBottom: 5,
	};

	if (!isOpen) return null;

	return (
		<Modal
			onClose={onClose}
			title={
				isEditing
					? `Modifier la vente flash : ${initialData?.name}`
					: "Programmer une nouvelle Vente Flash"
			}
			width={600}
		>
			<form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
				{/* Navigation Onglets — Douce et compacte */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr",
						gap: 3,
						padding: 3,
						background: "var(--a-surface-2)",
						border: "1px solid var(--a-border)",
						borderRadius: 9,
						margin: "12px 16px 0",
					}}
				>
					<button
						type="button"
						onClick={() => setActiveTab("product")}
						className="active:scale-[0.96]"
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							padding: "6px 10px",
							borderRadius: 6,
							fontSize: 12,
							fontWeight: activeTab === "product" ? 600 : 500,
							cursor: "pointer",
							border: activeTab === "product" ? "1px solid var(--a-border)" : "1px solid transparent",
							transition: "color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
							background: activeTab === "product" ? "var(--a-surface)" : "transparent",
							color: activeTab === "product" ? "var(--a-ink)" : "var(--a-ink-mute)",
							boxShadow: activeTab === "product" ? "var(--a-shadow-sm)" : "none",
						}}
					>
						1. Produit & Offre
					</button>
					<button
						type="button"
						onClick={() => setActiveTab("schedule")}
						className="active:scale-[0.96]"
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							padding: "6px 10px",
							borderRadius: 6,
							fontSize: 12,
							fontWeight: activeTab === "schedule" ? 600 : 500,
							cursor: "pointer",
							border: activeTab === "schedule" ? "1px solid var(--a-border)" : "1px solid transparent",
							transition: "color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
							background: activeTab === "schedule" ? "var(--a-surface)" : "transparent",
							color: activeTab === "schedule" ? "var(--a-ink)" : "var(--a-ink-mute)",
							boxShadow: activeTab === "schedule" ? "var(--a-shadow-sm)" : "none",
						}}
					>
						2. Dates & Stock Quota
					</button>
				</div>

				<div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
					{errorMessage && (
						<div
							style={{
								padding: "10px 14px",
								borderRadius: 8,
								background: "rgba(220, 38, 38, 0.1)",
								border: "1px solid rgba(220, 38, 38, 0.3)",
								color: "#dc2626",
								fontSize: 12,
								fontWeight: 600,
							}}
						>
							{errorMessage}
						</div>
					)}

					{/* TAB 1: PRODUIT & OFFRE */}
					{activeTab === "product" && (
						<div className="grid gap-3.5">
							<div>
								<label htmlFor="flash-sale-name" style={labelStyle}>
									Nom de l'opération Vente Flash <span style={{ color: "#d97706" }}>*</span>
								</label>
								<input
									id="flash-sale-name"
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="Ex. Vente Flash Smartphone -30%"
									className="aform-i w-full"
									style={{ color: "#0f172a", fontWeight: 600 }}
									required
								/>
							</div>

							<div>
								<label htmlFor="flash-sale-product" style={labelStyle}>
									Sélectionner le produit <span style={{ color: "#d97706" }}>*</span>
								</label>
								<select
									id="flash-sale-product"
									value={productId}
									onChange={(e) => setProductId(e.target.value)}
									className="aform-i w-full"
									style={{ color: "#0f172a", fontWeight: 600 }}
									required
								>
									<option value="">Choisir un produit...</option>
									{products.map((p) => (
										<option key={p.id} value={p.id}>
											{p.title} ({p.sku}) — {Number(p.basePrice).toLocaleString("fr-FR")} FCFA
										</option>
									))}
								</select>
							</div>

							<div className="grid grid-cols-2 gap-3">
								<div>
									<label htmlFor="flash-sale-discount-type" style={labelStyle}>
										Type de réduction
									</label>
									<select
										id="flash-sale-discount-type"
										value={discountType}
										onChange={(e) => setDiscountType(e.target.value as FlashSaleDiscountType)}
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 600 }}
									>
										<option value="PERCENT">Pourcentage (%)</option>
										<option value="FIXED">Montant de réduction (FCFA)</option>
										<option value="FIXED_PRICE">Prix Flash direct (FCFA)</option>
									</select>
								</div>

								<div>
									<label htmlFor="flash-sale-discount-value" style={labelStyle}>
										Valeur de l'offre <span style={{ color: "#d97706" }}>*</span>
									</label>
									<input
										id="flash-sale-discount-value"
										type="number"
										min="0"
										max={discountType === "PERCENT" ? 100 : undefined}
										value={discountValue}
										onChange={(e) =>
											setDiscountValue(e.target.value === "" ? "" : Number(e.target.value))
										}
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 700 }}
										required
									/>
								</div>
							</div>

							{selectedProduct && (
								<div
									style={{
										background: "var(--a-terra-soft)",
										border: "1px solid rgba(198, 86, 58, 0.3)",
									}}
									className="p-3.5 rounded-lg flex items-center justify-between"
								>
									<div>
										<span className="block text-[11px] text-[var(--a-terra-deep)] font-medium">
											Aperçu du prix Vente Flash :
										</span>
										<div className="flex items-baseline gap-2 mt-0.5">
											<span className="text-base font-bold text-[var(--a-terracotta)] font-mono">
												{calculatedFlashPrice.toLocaleString("fr-FR")} FCFA
											</span>
											<span className="text-xs line-through text-slate-400 font-mono">
												{Number(selectedProduct.basePrice).toLocaleString("fr-FR")} FCFA
											</span>
										</div>
									</div>
									<div className="text-right text-[11px] font-bold text-emerald-700 font-mono">
										−{Math.round(100 - (calculatedFlashPrice / Number(selectedProduct.basePrice)) * 100)}% d'économie
									</div>
								</div>
							)}
						</div>
					)}

					{/* TAB 2: DATES & STOCK */}
					{activeTab === "schedule" && (
						<div className="grid gap-3.5">
							<div className="grid grid-cols-2 gap-3">
								<div>
									<label htmlFor="flash-sale-starts-at" style={labelStyle}>
										Date & heure de début <span style={{ color: "#d97706" }}>*</span>
									</label>
									<input
										id="flash-sale-starts-at"
										type="datetime-local"
										value={startsAt}
										onChange={(e) => setStartsAt(e.target.value)}
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 500 }}
										required
									/>
								</div>

								<div>
									<label htmlFor="flash-sale-expires-at" style={labelStyle}>
										Date & heure de fin <span style={{ color: "#d97706" }}>*</span>
									</label>
									<input
										id="flash-sale-expires-at"
										type="datetime-local"
										value={expiresAt}
										onChange={(e) => setExpiresAt(e.target.value)}
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 500 }}
										required
									/>
								</div>
							</div>

							<div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200">
								<div>
									<label htmlFor="flash-sale-stock-limit" style={labelStyle}>
										Quota de stock réservé (unités)
									</label>
									<input
										id="flash-sale-stock-limit"
										type="number"
										min="0"
										value={stockLimit}
										onChange={(e) =>
											setStockLimit(e.target.value === "" ? "" : Number(e.target.value))
										}
										placeholder="0 = illimité"
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 600 }}
									/>
									<span className="block text-[10px] text-slate-400 mt-1">
										0 = pas de limite d'unités pendant la période
									</span>
								</div>

								<div>
									<label htmlFor="flash-sale-priority" style={labelStyle}>
										Priorité d'affichage
									</label>
									<input
										id="flash-sale-priority"
										type="number"
										min="1"
										max="1000"
										value={priority}
										onChange={(e) =>
											setPriority(e.target.value === "" ? "" : Number(e.target.value))
										}
										placeholder="10"
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 600 }}
									/>
								</div>
							</div>

							<div className="pt-3 border-t border-slate-200">
								<label className="flex items-center gap-2.5 cursor-pointer">
									<input
										type="checkbox"
										checked={isActive}
										onChange={(e) => setIsActive(e.target.checked)}
										style={{ accentColor: "var(--a-terracotta)" }}
										className="w-4 h-4 rounded cursor-pointer"
									/>
									<span style={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
										Activer cette vente flash immédiatement
									</span>
								</label>
							</div>
						</div>
					)}
				</div>

				<ModalFooter>
					<AdminButton
						variant="ghost"
						type="button"
						onClick={onClose}
						disabled={isPending}
					>
						Annuler
					</AdminButton>
					<AdminButton
						variant="primary"
						type="submit"
						disabled={isPending}
					>
						{isEditing ? "Enregistrer" : "Créer la vente flash"}
					</AdminButton>
				</ModalFooter>
			</form>
		</Modal>
	);
}
